/**
 * Client-side guards for envelope-encrypted fields (BUG-135).
 *
 * The API's envelope layer (lambda/innovateflow-api/shared/envelope.py) fails
 * CLOSED on decryption errors: it REMOVES the unreadable field and stamps the
 * item with `_decrypt_error: true`, `_decrypt_error_reason` and
 * `_decrypt_failed_fields`. Two legacy paths can still leak raw base64
 * ciphertext to the UI, though: items marked `_cmk_encrypted` but missing DEK
 * metadata are returned unmodified, and rows encrypted before the fail-closed
 * fix may already hold ciphertext in plaintext columns. Rendering such a value
 * shows the user an undecipherable base64 blob.
 *
 * UI code rendering a sensitive field must therefore check BOTH markers via
 * these helpers and show a friendly "encrypted — cannot display" state.
 */

// AES-GCM output is base64(nonce[12] + ciphertext + tag[16]); even a very
// short plaintext encodes to 50+ base64 chars with no spaces. Real user text
// (declarations, descriptions) virtually always contains whitespace well
// before 48 chars, so a long, unbroken base64 run is a reliable ciphertext
// signal.
const BASE64_BLOB = /^[A-Za-z0-9+/]{48,}={0,2}$/

/** True when a string value looks like raw envelope ciphertext, not prose. */
export function looksLikeCiphertext(value: unknown): boolean {
  if (typeof value !== 'string') return false
  return BASE64_BLOB.test(value.trim())
}

/** True when the backend explicitly flagged `field` as undecryptable on `item`. */
export function fieldDecryptFailed(
  item: { _decrypt_error?: boolean; _decrypt_failed_fields?: string[] } | null | undefined,
  field: string,
): boolean {
  if (!item?._decrypt_error) return false
  const failed = item._decrypt_failed_fields
  // A DEK-level failure may omit the field list — treat every sensitive field
  // as failed in that case (mirrors envelope.py's redaction semantics).
  return !failed || failed.includes(field)
}
